import { faChevronDown } from "@jewlr/pro-solid-svg-icons/faChevronDown"
import { faChevronUp } from "@jewlr/pro-solid-svg-icons/faChevronUp"
import { Expand } from "@jewlr/storybook/animate"
import {
  Box,
  Button,
  Divider,
  Flex,
  FontAwesomeIcon,
  Image,
  MediaBox,
  Picture,
  Typography,
} from "@jewlr/storybook/core"
import PropTypes from "prop-types"
import React, { useState } from "react"
import styled from "styled-components"

import withDetails from "areas/product/hoc/with-details"
import { Section, SectionTitle, Text } from "areas/product/jewlr/components"
import { jewlr_t } from "helpers/application"
import { to_currency } from "helpers/number"

const PriceDisplay = styled(Typography).attrs((props) => ({
  as: "h3",
  fontSize: { _: "30px", desktop: "30px", tablet: "26px" },
  fontWeight: 500,
  letterSpacing: "-0.05em",
  lineHeight: { _: "32px", desktop: "32px", tablet: "28px" },
  textAlign: "center",
  ...props,
}))``
const PriceLabel = styled(Typography).attrs({
  fontSize: "16px",
  fontWeight: 300,
  letterSpacing: "-0.02em",
  lineHeight: "22px",
  textAlign: "center",
})``

const StyledSection = styled(Section).attrs((props) => ({
  border: {
    _: `1px solid ${props.theme.colors.grey[10]}`,
    tablet: "none",
  },
  borderRadius: { _: "10px", tablet: 0 },
  mx: { _: 2, tablet: 0 },
  my: { _: 4, tablet: 0 },
  pb: { _: 3, tablet: 6 },
  pt: 6,
  textAlign: "center",
}))``

const LearnHow = styled(Button).attrs({
  color: "primary",
  fontSize: "16px",
  fontWeight: "500",
  letterSpacing: "-0.02em",
  lineHeight: "22px",
  variant: "text",
})``

const DeliverValue = ({ description, pricing }) => {
  const [showDetails, setShowDetails] = useState(false)

  const productImage = (
    <Image
      alt={description.style_code || "Deliver value"}
      ratio="325 / 325"
      src={description.product_default_img}
      title={description.style_code}
      width={{ _: "188px", desktop: "200px", tablet: "160px" }}
    />
  )

  const modelDescription = (
    <Text
      fontSize={{ _: "20px", tablet: "16px" }}
      fontWeight="300"
      lineHeight={{ _: "24px", tablet: "20px" }}
      mb={{ _: 6, tablet: 0 }}
      mx="auto"
      textAlign="center"
    >
      We eliminated the middle steps, allowing us to drive value to our
      customers.
    </Text>
  )

  const stepImages = (
    <>
      <Picture
        alt={`At Jewlr, we manufacture our ${jewlr_t(
          "terms.jewelry"
        )} in Canada and sell it online directly to our customers, eliminating the middle steps so you get quality products at low prices, every day.`}
        fallback={require("images/product/honest_pricing/jewlr-model-flow.svg")}
        mb={{ _: 4, tablet: 0 }}
        mx="auto"
        srcSet={[
          {
            height: "181",
            media: "(max-width: 767px)",
            sizes: "100vw",
            srcSet: `${require("images/product/honest_pricing/m-jewlr-pricing-model.svg")} 321w`,
            width: "321",
          },
          {
            height: "214",
            media: "(min-width: 768px)",
            sizes: "402px",
            srcSet: `${require("images/product/honest_pricing/jewlr-model-flow.svg")} 562w`,
            width: "562",
          },
        ]}
        width={{ _: "100%", tablet: "402px" }}
      />
      <Divider color="black" display={{ _: "none", tablet: "block" }} mb={3} />
      <Picture
        alt="In traditional retail, products go from manufacturing to wholesale to distributors to retail stores – each step adds more costs, resulting in higher everyday pricing for customers."
        fallback={require("images/product/honest_pricing/traditional-model-flow.svg")}
        mx="auto"
        srcSet={[
          {
            height: "288",
            media: "(max-width: 767px)",
            sizes: "100vw",
            srcSet: `${require("images/product/honest_pricing/m-traditional-retail-model.svg")} 329w`,
            width: "329",
          },
          {
            height: "186",
            media: "(min-width: 768px)",
            sizes: "460px",
            srcSet: `${require("images/product/honest_pricing/traditional-model-flow.svg")} 684w`,
            width: "684",
          },
        ]}
        width={{ _: "100%", tablet: "460px" }}
      />
    </>
  )

  return (
    <StyledSection>
      <SectionTitle mb={3} textAlign="center">
        WE DELIVER GREAT VALUE
      </SectionTitle>
      <Flex
        alignItems="center"
        flexDirection={{ _: "column", tablet: "row" }}
        justifyContent="center"
      >
        <MediaBox
          flex="1"
          greaterThanOrEqual="tablet"
          pr={{ _: 2, desktop: 4 }}
        >
          <Flex
            alignItems="center"
            gap={{ _: 2, desktop: 4 }}
            justifyContent="center"
            mb="10px"
          >
            <Box>
              <PriceDisplay color="black">
                {to_currency(pricing.price_to_display)}
              </PriceDisplay>
              <PriceLabel color="black">Jewlr Price</PriceLabel>
            </Box>
            {productImage}
            <Box>
              <PriceDisplay color="#828282">
                {to_currency(pricing.retail_price)}
              </PriceDisplay>
              <PriceLabel color="#828282">Retail Price</PriceLabel>
            </Box>
          </Flex>
          {modelDescription}
        </MediaBox>
        <MediaBox
          borderLeft="1px solid #d9d9d9"
          flex="1"
          greaterThanOrEqual="tablet"
          pl={{ _: 2, desktop: 4 }}
        >
          <Box textAlign="center">{stepImages}</Box>
        </MediaBox>
        <MediaBox lessThan="tablet" px={4}>
          <Box textAlign="center">
            {productImage}
            <Flex alignItems="center" justifyContent="center" my="12px">
              <Box borderRight="1px solid #d9d9d9" pr={6} py="12px">
                <PriceDisplay color="black">
                  {to_currency(pricing.price_to_display)}
                </PriceDisplay>
                <PriceLabel color="black">Jewlr Price</PriceLabel>
              </Box>
              <Box pl={6}>
                <PriceDisplay color="#828282">
                  {to_currency(pricing.retail_price)}
                </PriceDisplay>
                <PriceLabel color="#828282">Retail Price</PriceLabel>
              </Box>
            </Flex>
            <LearnHow onClick={() => setShowDetails(!showDetails)}>
              Learn How We Do It
              <FontAwesomeIcon
                color="primary"
                fontSize="10px"
                icon={showDetails ? faChevronUp : faChevronDown}
                ml="6px"
              />
            </LearnHow>
          </Box>
          <Expand show={showDetails}>
            <Box mb={6} mt={7} textAlign="center">
              <Typography
                fontSize="28px"
                fontWeight="700"
                lineHeight="32px"
                mb={2}
              >
                Jewlr vs. Traditional Retailer
              </Typography>
              {modelDescription}
              {stepImages}
            </Box>
          </Expand>
        </MediaBox>
      </Flex>
    </StyledSection>
  )
}

DeliverValue.propTypes = {
  description: PropTypes.object,
  pricing: PropTypes.object,
}

export default withDetails(DeliverValue)
